import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, MapPin, CalendarDays, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { loginTo } from "./data";

const STATUS_LABEL = { confirmed: "Live", open: "Upcoming", awaiting_opponent: "Upcoming", awaiting_booking: "Upcoming", completed: "Final" };

export default function LiveMatchStrip() {
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    api.get("/public/matches").then((r) => setMatches(Array.isArray(r.data) ? r.data : [])).catch(() => setMatches([]));
  }, []);

  if (!matches || matches.length === 0) return null;

  return (
    <section className="border-y border-border bg-foreground">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-background">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
            </span>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Matches happening now</h2>
          </div>
          <Link to={loginTo("/app/matches")} data-testid="live-view-all"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold text-background transition-colors hover:bg-white/10">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="no-scrollbar mt-6 flex gap-4 overflow-x-auto pb-2">
          {matches.map((m) => {
            const label = STATUS_LABEL[m.status] || "Match";
            const hasScore = m.home_score != null && m.away_score != null;
            return (
              <Link key={m.id} to={loginTo("/app/matches")} data-testid={`live-match-${m.id}`}
                className="group w-72 shrink-0 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur transition-all hover:bg-white/10">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-semibold capitalize text-primary">{m.sport}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${
                    m.is_live ? "bg-red-500/20 text-red-400" : label === "Final" ? "bg-white/10 text-background/70" : "bg-emerald-500/15 text-emerald-400"}`}>
                    {m.is_live && <Radio className="h-3 w-3" />} {m.is_live ? "LIVE" : label}
                  </span>
                </div>
                <h3 className="mt-3 line-clamp-2 min-h-[2.5rem] font-display text-lg font-bold leading-tight text-background">{m.title}</h3>
                {(m.home_team_name || m.away_team_name) && (
                  <div className="mt-2 flex items-center justify-between gap-2 text-sm text-background/80">
                    <span className="truncate">{m.home_team_name || "TBD"}</span>
                    {hasScore
                      ? <span className="shrink-0 font-display text-lg font-extrabold text-background">{m.home_score} - {m.away_score}</span>
                      : <span className="shrink-0 text-xs text-background/50">vs</span>}
                    <span className="truncate text-right">{m.away_team_name || "TBD"}</span>
                  </div>
                )}
                <div className="mt-3 space-y-1 text-xs text-background/60">
                  {m.venue && <p className="flex items-center gap-1"><MapPin className="h-3 w-3" /> <span className="truncate">{m.venue}</span></p>}
                  {m.date && <p className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {m.date}{m.start ? ` · ${m.start}` : ""}</p>}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
